import { useEffect, useRef } from 'react';
import AnalyticsManager from '../services/AnalyticsManager';
import { useUser } from '../Widgets/UserContext';

/**
 * Hook to automatically track page views in React components
 * @param {string} pageName - The name of the page to track
 * @param {object} pageParams - Additional parameters to track with the page view
 * @param {object} options - Options for tracking behavior
 */
export const usePageTracking = (pageName, pageParams = {}, options = {}) => {
  const { 
    trackOnMount = true, 
    trackOnParamsChange = true,
    debounceMs = 1000 
  } = options;
  
  const { currentUser, currentUserData } = useUser();
  const lastTrackedRef = useRef(null);
  const timeoutRef = useRef(null);

  const trackPage = async () => {
    if (!pageName) return;
    
    const trackingData = {
      pageName,
      params: {
        ...pageParams,
        // Add user context information
        userId: currentUser?.uid || 'anonymous', // Firebase Auth UID
        userCustomUID: currentUserData?.uid, // MyTassel custom UID (like G25736)
        customUID: currentUserData?.uid, // Alias for custom UID
        userDisplayName: currentUserData?.firstName && currentUserData?.lastName 
          ? `${currentUserData.firstName} ${currentUserData.lastName}`
          : currentUserData?.uid || 'Anonymous User',
        isAuthenticated: !!currentUser && !currentUser.isAnonymous,
        viewerType: currentUserData?.uid ? 'owner' : 'visitor' // Default, can be overridden by pageParams
      },
      timestamp: new Date().toISOString(),
      url: window?.location?.href || 'unknown',
      pathname: window?.location?.pathname || 'unknown',
      userAgent: navigator?.userAgent || 'unknown'
    };

    // Debug logging
    console.log('📊 Page tracking data:', {
      pageName,
      currentUser: currentUser ? { uid: currentUser.uid } : null,
      currentUserData: currentUserData ? { uid: currentUserData.uid, firstName: currentUserData.firstName } : null,
      params: trackingData.params
    });

    // Check if we should track (debounce duplicate calls)
    const trackingKey = JSON.stringify(trackingData);
    if (lastTrackedRef.current === trackingKey) {
      return;
    }

    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Debounce the tracking call
    timeoutRef.current = setTimeout(async () => {
      try {
        await AnalyticsManager.trackPageView(pageName, trackingData.params);
        lastTrackedRef.current = trackingKey;
        console.log(`🎯 Page tracked: ${pageName}`);
      } catch (error) {
        console.error(`Failed to track page ${pageName}:`, error);
      }
    }, debounceMs);
  };

  // Track on mount
  useEffect(() => {
    if (trackOnMount) {
      trackPage();
    }

    // Cleanup timeout on unmount
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [pageName]); // Only depend on pageName for mount tracking

  // Track on params change
  useEffect(() => {
    if (trackOnParamsChange && lastTrackedRef.current !== null) {
      trackPage();
    }
  }, [JSON.stringify(pageParams)]); // Track when params change

  // Return manual tracking function
  return {
    trackPage: () => trackPage(),
    forceTrack: async () => {
      lastTrackedRef.current = null; // Reset to force tracking
      await trackPage();
    }
  };
};

/**
 * Hook to track specific events on a page
 * @param {string} pageName - The page name context for events
 */
export const useEventTracking = (pageName) => {
  const trackEvent = async (eventName, eventData = {}) => {
    try {
      const fullEventData = {
        ...eventData,
        pageName,
        timestamp: new Date().toISOString(),
        url: window?.location?.href || 'unknown'
      };

      // For now, we'll track events as special page views
      // This can be extended to use a dedicated event tracking method
      await AnalyticsManager.trackPageView(`${pageName}:${eventName}`, fullEventData);
      console.log(`📊 Event tracked: ${eventName} on ${pageName}`);
    } catch (error) {
      console.error(`Failed to track event ${eventName}:`, error);
    }
  };

  return { trackEvent };
};

export default usePageTracking;